<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchGrant,
  createGrant,
  updateGrant,
  fetchRoles,
  fetchGrantRoles,
  fetchGrantRole,
  createGrantRole,
  deleteGrantRole,
} from '@apis/admin/acs'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { LangItem },
  props: {
    grant_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      roles: [],
      grant_roles: [],
      role_params: null,
      member_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadGrant(), this.loadRoles()])
        .then(() => this.loadGrantRoles())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadGrant() {
      const { grant_info } = await fetchGrant(this.grant_seq)
      this.params = defaults(grant_info, {
        name: '',
        title: '',
        seq: null,
        regdate: null,
      })
      this.info = grant_info
    },
    async loadRoles() {
      const { data } = await fetchRoles()

      this.roles = data
    },
    async loadGrantRoles() {
      if (!this.info) return []

      const { data } = await fetchGrantRoles(this.info.seq)

      this.grant_roles = data
    },
    async loadGrantRole(role_seq) {
      const { data } = await fetchGrantRole(this.info.seq, role_seq)

      this.role_params = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateGrant : createGrant
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_grant_modify')
        : this.$t('admin.msg_grant_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddRole() {
      this.grant_roles.push({
        grant_seq: this.info.seq,
        role_seq: undefined,
      })
    },
    async handleDeleteRole(idx) {
      this.loading = true
      try {
        if (this.grant_roles[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteGrantRole(this.info.seq, this.grant_roles[idx].seq)
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.grant_roles.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitRole(idx) {
      this.loading = true
      const params = this.grant_roles[idx]
      createGrantRole(params)
        .then(({ seq }) => fetchGrantRole(this.info.seq, seq))
        .then(({ role_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.grant_roles, idx, { ...role_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.user_name') }}</div></th
              >
              <td>
                <template v-if="params.seq">{{ params.name }}</template>
                <input
                  v-else
                  v-model="params.name"
                  type="text"
                  :class="[$style.inputTypeText]"
                />
              </td>
              <th scope="row"
                ><div>{{ $t('admin.title') }}</div></th
              >
              <td
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('admin.role')"
      name="CONDITION"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">{{ $t('admin.role') }}</th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddRole"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(grant_role, idx) in grant_roles"
            :key="grant_role.seq ? `${grant_role.seq}` : `create-${idx}`"
          >
            <td :class="$style.wide">
              <select v-model="grant_role.role_seq" :disabled="grant_role.seq">
                <option :value="undefined">{{ $t('admin.select') }}</option>
                <option v-for="val in roles" :key="val.seq" :value="val.seq">
                  {{ val.title }}
                </option>
              </select>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="!grant_role.seq"
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitRole(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteRole(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!grant_roles.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('admin.msg_grant_roles')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
